/**
 * Build a human-friendly display name for the selected teacher model.
 *
 * For Bedrock we look up the selected entry in the provider's catalog so we
 * can surface a richer label (today this preserves the legacy ``id (id)``
 * format; once the picker is wired to display vendor info, this can become
 * something like ``id (vendor)``). For other providers the saved value is
 * already the display name.
 */
export function buildTeacherModelDisplay({
    provider,
    teacherModelId,
    bedrockCatalog = [],
    fallbackDisplay = null,
}) {
    if (!teacherModelId) return '';

    if (provider === 'bedrock') {
        const selected = (bedrockCatalog || []).find(
            (m) => m?.id === teacherModelId
        );
        if (selected?.id) {
            return `${selected.id} (${selected.id})`;
        }
        return fallbackDisplay || `${teacherModelId}`;
    }

    return `${teacherModelId}`;
}

const PROVIDER_LABELS = {
    openai: 'OpenAI',
    bedrock: 'Bedrock',
    anthropic: 'Anthropic',
    google: 'Google',
    azure: 'AzureAI',
    vertex: 'VertexAI',
    deepseek: 'DeepSeek',
    jev: 'TypeSafe AI',
    specificai: 'SpecificAI',
};

const VENDOR_LABELS = {
    openai: 'OpenAI',
    anthropic: 'Anthropic',
    google: 'Google',
    mistralai: 'Mistral AI',
    meta: 'Meta',
    microsoft: 'Microsoft',
    cohere: 'Cohere',
    ai21: 'AI21 Labs',
    deepseek: 'DeepSeek',
    xai: 'xAI',
    amazon: 'Amazon',
    typesafe: 'TypeSafe AI',
};

export function providerLabelFor(provider) {
    if (!provider) return '';
    return PROVIDER_LABELS[provider] || provider;
}

function vendorLabelFor(vendor) {
    if (vendor == null || vendor === '') return '';
    return VENDOR_LABELS[String(vendor).toLowerCase()] || vendor;
}

/**
 * Commercial Model label for the Train / Model Summary tab (Figma 3001-68512).
 *
 * Vendor: ``gpt-3.5-turbo, OpenAI``
 * Cloud:  ``gpt-3.5-turbo, OpenAI, AzureAI``
 * Empty:  ``----``
 */
export function formatCommercialModelLabel({
    teacherModel,
    teacherProvider,
    availableProviders = {},
}) {
    if (!teacherModel) return '----';

    const providerKey = teacherProvider || 'openai';
    const providerInfo = availableProviders?.[providerKey];
    const providerLabel = providerLabelFor(providerKey);
    const parts = [teacherModel];

    if (providerInfo?.provider_type === 'cloud') {
        const modelEntry = (providerInfo.models || []).find(
            (m) => m?.id === teacherModel
        );
        const vendorLabel = vendorLabelFor(modelEntry?.vendor);
        if (vendorLabel) parts.push(vendorLabel);
        if (providerLabel) parts.push(providerLabel);
    } else if (providerLabel) {
        parts.push(providerLabel);
    }

    return parts.join(', ');
}

/**
 * Model Summary status for teacher comparison.
 *
 * Vendor: ``Active (gpt-3.5-turbo, OpenAI)``
 * Cloud:  ``Active (gpt-3.5-turbo, OpenAI, AzureAI)`` — model, vendor, cloud provider
 * Off:    ``Inactive``
 */
export function formatTeacherComparisonStatus({
    isActive,
    teacherModel,
    teacherProvider,
    availableProviders = {},
}) {
    if (!isActive) return 'Inactive';
    if (!teacherModel) return 'Active';

    return `Active (${formatCommercialModelLabel({
        teacherModel,
        teacherProvider,
        availableProviders,
    })})`;
}

/**
 * Short ``model (Provider)`` teacher label, e.g. ``o3 (AzureAI)``.
 * Empty string when no teacher model is configured.
 */
export function formatTeacherModelShortLabel({ teacherModel, teacherProvider }) {
    const model = (teacherModel || '').toString().trim();
    if (!model) return '';
    const providerLabel = providerLabelFor(teacherProvider || 'openai');
    return providerLabel ? `${model} (${providerLabel})` : model;
}

/**
 * Re-eval modal status when teacher comparison is on.
 * Matches Model Selection picker labels, e.g. ``Comparing against gpt-4 (Open AI)``.
 */
export function formatComparingAgainstStatus({ teacherModel, teacherProvider }) {
    const model = (teacherModel || '').toString().trim();
    if (!model) return 'Comparing against a commercial model';
    const providerKey = teacherProvider || 'openai';
    const pickerLabels = { ...PROVIDER_LABELS, openai: 'Open AI' };
    const providerLabel = pickerLabels[providerKey] || providerKey;
    return `Comparing against ${model} (${providerLabel})`;
}
